import { nextTick, onMounted, onUnmounted, type Ref } from 'vue';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

function createSentinel(): HTMLElement {
  const el = document.createElement('div');
  el.tabIndex = 0;
  Object.assign(el.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: '0',
    padding: '0',
    margin: '-1px',
  });
  return el;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden')
  );
}

export function useFocusTrap(containerRef: Ref<HTMLElement | undefined>) {
  let sentinelEl: HTMLElement | null = null;
  let previouslyFocusedElement: HTMLElement | null = null;
  let enabled = false;

  function insertSentinel(container: HTMLElement) {
    sentinelEl = createSentinel();
    container.insertBefore(sentinelEl, container.firstChild);
    sentinelEl.focus();
  }

  function removeSentinel(container: HTMLElement) {
    if (sentinelEl && container.contains(sentinelEl)) {
      container.removeChild(sentinelEl);
      sentinelEl = null;
    }
  }

  function trapFocus(event: KeyboardEvent) {
    const container = containerRef.value;
    if (event.key !== 'Tab' || !container) {
      return;
    }

    const focusables = getFocusableElements(container);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) {
      return;
    }

    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async function enable() {
    if (!enabled) {
      enabled = true;
      document.addEventListener('keydown', trapFocus);
    }

    previouslyFocusedElement = document.activeElement as HTMLElement | null;

    await nextTick();

    if (containerRef.value && !sentinelEl) {
      insertSentinel(containerRef.value);
    }
  }

  function disable(options?: { restoreFocus?: boolean }) {
    if (!enabled) {
      return;
    }

    enabled = false;
    document.removeEventListener('keydown', trapFocus);
    const container = containerRef.value;
    if (container) {
      removeSentinel(container);
    }

    if (options?.restoreFocus !== false) {
      const isFocusInside = !!container && container.contains(document.activeElement);
      if (isFocusInside && previouslyFocusedElement?.isConnected) {
        try {
          previouslyFocusedElement.focus({ preventScroll: true });
        } catch {}
      }
    }

    previouslyFocusedElement = null;
  }

  onMounted(enable);
  onUnmounted(() => disable());
  return { enable, disable };
}
